import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import styled from 'styled-components';

const PageSection = styled.section`
  padding-top: 70px;
  background-color: #f5f5f5;
  min-height: 100vh;
`;

const CategoryTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  color: var(--primary-color);
  font-size: 2.5rem;
  font-weight: 700;
`;

const Divider = styled.div`
  width: 80px;
  height: 3px;
  background-color: #4a6163;
  border-radius: 2px;
  margin: 0.75rem auto 0;
`;

const ProductCard = styled.div`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(74, 97, 99, 0.10);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.3s ease, transform 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(74, 97, 99, 0.18);
    transform: translateY(-4px);
  }
`;

const ProductCardMain = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const ProductCardCTA = styled.div`
  background: linear-gradient(135deg, #4a6163 0%, #5d7a7c 100%);
  padding: 1.25rem 2.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.88rem;
    line-height: 1.5;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    text-align: center;
    padding: 1.2rem;
  }
`;

const ProductImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  flex: 0 0 42%;
  min-height: 300px;
  background-color: #edf0ef;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
  }

  ${ProductCard}:hover & img {
    transform: scale(1.04);
  }

  @media (max-width: 767px) {
    flex: none;
    height: 300px;
    min-height: unset;
  }
`;

const ProductBadge = styled.span`
  position: absolute;
  top: 16px;
  left: 16px;
  background-color: #4a6163;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 20px;
`;

const ProductBody = styled.div`
  padding: 2.5rem 2.8rem;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;

  @media (max-width: 767px) {
    padding: 1.4rem;
  }
`;

const ProductName = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 1.75rem;
  color: #2c3e3f;
  margin-bottom: 0.25rem;
`;

const ProductSubtitle = styled.p`
  font-size: 0.88rem;
  color: #6c7a7b;
  margin-bottom: 0.75rem;
  font-style: italic;
`;

const ProductDescription = styled.p`
  font-size: 0.93rem;
  color: #555;
  line-height: 1.65;
  margin-bottom: 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.75rem 0 1.2rem;

  li {
    font-size: 0.88rem;
    color: #555;
    padding: 0.2rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &::before {
      content: '';
      display: inline-block;
      width: 7px;
      height: 7px;
      background-color: #4a6163;
      border-radius: 50%;
      flex-shrink: 0;
    }
  }
`;

const ContactCTA = styled.div`
  background: linear-gradient(135deg, #4a6163 0%, #5d7a7c 100%);
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  color: #fff;
  margin-top: 4rem;

  h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.6rem;
    margin-bottom: 0.75rem;
  }

  p {
    opacity: 0.88;
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
  }
`;

const plantas = [
  {
    id: 'planta-faux-1',
    name: 'Planta Faux Tropical',
    subtitle: 'Decoración interior sin mantenimiento',
    image: '/images/planta.png',
    badge: 'Disponible',
    description:
      'Plantas artificiales de alta calidad diseñadas para lucir naturales en cualquier espacio. Ideales para interiores donde la luz natural es limitada o simplemente quieres disfrutar del verde sin preocuparte por el riego.',
    features: [
      'Aspecto hiperrealista, indistinguible de plantas naturales',
      'Sin necesidad de riego ni luz solar',
      'Resistente al polvo y de fácil limpieza',
      'Perfecta para salas, oficinas, lobbies y comedores',
      'Disponible en distintos tamaños',
    ],
  },
];

const ProductosPage = () => {
  return (
    <PageSection>
      <Container className="py-5">

        {/* Sección Plantas Faux */}
        <div className="mb-5">
          <div className="text-center mb-4">
            <CategoryTitle>Plantas Faux</CategoryTitle>
            <Divider />
          </div>

          <Row className="g-4">
            {plantas.map((planta) => (
              <Col key={planta.id} xs={12}>
                <ProductCard>
                  <ProductCardMain>
                    <ProductImageWrapper>
                      <img src={planta.image} alt={planta.name} />
                      <ProductBadge>{planta.badge}</ProductBadge>
                    </ProductImageWrapper>
                    <ProductBody>
                      <ProductName>{planta.name}</ProductName>
                      <ProductSubtitle>{planta.subtitle}</ProductSubtitle>
                      <ProductDescription>{planta.description}</ProductDescription>
                      <FeatureList>
                        {planta.features.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </FeatureList>
                    </ProductBody>
                  </ProductCardMain>
                  <ProductCardCTA>
                    <p>¿Buscas algo específico para tu espacio? Cuéntanos tu proyecto y te ayudamos a encontrar los productos ideales.</p>
                    <Button
                      as={Link}
                      to="/contacto"
                      className="fw-semibold rounded-pill flex-shrink-0"
                      style={{ backgroundColor: '#fff', borderColor: '#fff', color: '#4a6163', padding: '0.5rem 1.5rem' }}
                    >
                      Contáctanos
                    </Button>
                  </ProductCardCTA>
                </ProductCard>
              </Col>
            ))}
          </Row>
        </div>

      </Container>
    </PageSection>
  );
};

export default ProductosPage;
